// Campaign API Service
import { createApiClient } from '../utils/axiosConfig';
import { getApiBaseUrl } from '../utils/apiConfig';

// Use base server URL - normalize to remove trailing /api if present
// Endpoints will include /api prefix
let API_BASE_URL = getApiBaseUrl();
// Remove trailing /api if present to avoid double /api/api/
if (API_BASE_URL.endsWith('/api')) {
  API_BASE_URL = API_BASE_URL.slice(0, -4);
}

// Create axios instance with default config and auth error handling
const apiClient = createApiClient(API_BASE_URL);

// Types
export interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  startDate: string;
  endDate: string;
  budget: number;
  spent: number;
  targetAudience: string;
  channels: number;
  prs: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cpc: number;
  roi: number;
  createdBy: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCampaignRequest {
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  budget: number;
  targetAudience: string;
  tags?: string[];
  pressReleases?: string[];
}

export interface UpdateCampaignRequest {
  name?: string;
  description?: string;
  status?: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  startDate?: string;
  endDate?: string;
  budget?: number;
  targetAudience?: string;
  tags?: string[];
  pressReleases?: string[];
}

export interface CampaignStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalBudget: number;
  totalSpent: number;
  totalImpressions: number;
}

// Campaign API Functions

/**
 * Get all campaigns for the authenticated user
 */
export const getCampaigns = async (filters?: {
  status?: string;
  search?: string;
}): Promise<Campaign[]> => {
  try {
    const params = new URLSearchParams();
    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }

    const url = `/api/campaigns${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.campaigns || [];
  } catch (error: any) {
    console.error('Error fetching campaigns:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch campaigns');
  }
};

/**
 * Get a single campaign by ID
 */
export const getCampaign = async (id: string): Promise<Campaign> => {
  try {
    const response = await apiClient.get(`/api/campaigns/${id}`);
    return response.data.campaign;
  } catch (error: any) {
    console.error('Error fetching campaign:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch campaign');
  }
};

/**
 * Create a new campaign
 */
export const createCampaign = async (
  data: CreateCampaignRequest
): Promise<Campaign> => {
  try {
    const response = await apiClient.post('/api/campaigns', data);
    return response.data.campaign;
  } catch (error: any) {
    console.error('Error creating campaign:', error);
    throw new Error(error.response?.data?.message || 'Failed to create campaign');
  }
};

/**
 * Update a campaign
 */
export const updateCampaign = async (
  id: string,
  data: UpdateCampaignRequest
): Promise<Campaign> => {
  try {
    const response = await apiClient.put(`/api/campaigns/${id}`, data);
    return response.data.campaign;
  } catch (error: any) {
    console.error('Error updating campaign:', error);
    throw new Error(error.response?.data?.message || 'Failed to update campaign');
  }
};

/**
 * Delete a campaign
 */
export const deleteCampaign = async (id: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/campaigns/${id}`);
  } catch (error: any) {
    console.error('Error deleting campaign:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete campaign');
  }
};

/**
 * Update campaign status
 */
export const updateCampaignStatus = async (
  id: string,
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled'
): Promise<Campaign> => {
  try {
    const response = await apiClient.patch(`/api/campaigns/${id}/status`, { status });
    return response.data.campaign;
  } catch (error: any) {
    console.error('Error updating campaign status:', error);
    throw new Error(error.response?.data?.message || 'Failed to update campaign status');
  }
};

/**
 * Add press release to campaign
 */
export const addPressReleaseToCampaign = async (
  campaignId: string,
  pressReleaseId: string
): Promise<void> => {
  try {
    await apiClient.post(`/api/campaigns/${campaignId}/press-releases`, {
      pressReleaseId
    });
  } catch (error: any) {
    console.error('Error adding press release to campaign:', error);
    throw new Error(error.response?.data?.message || 'Failed to add press release to campaign');
  }
};

/**
 * Get campaign statistics
 */
export const getCampaignStats = async (): Promise<CampaignStats> => {
  try {
    const response = await apiClient.get('/api/campaigns/stats');
    return response.data.stats;
  } catch (error: any) {
    console.error('Error fetching campaign stats:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch campaign stats');
  }
};

export default {
  getCampaigns,
  getCampaign,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  updateCampaignStatus,
  addPressReleaseToCampaign,
  getCampaignStats
};














